import React, { useMemo, useState } from "react";
import clsx from "clsx";
import { Bell } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDesignSystem } from "../lib/design/designSystem";
import {
    useAdminNotifications,
    useCanSeeNotifications,
} from "../hooks/useAdminNotifications";
import NotificationCenterPanel from "./NotificationCenterPanel";

/**
 * Bell unit for the league-admin top bar. Mirrors the jobs button + JobCenterPanel
 * pairing in Header.tsx: renders a bell with an amber pending-count badge, owns its
 * own open/close state, and conditionally renders the dropdown panel.
 *
 * Self-gates on useCanSeeNotifications (Header uses the same hook, so this is
 * defensive); each notification source is gated by its own rule. The badge count
 * and panel share the same react-query cache via useAdminNotifications, so treating
 * an item (verify/reject/review) invalidates those keys and the badge auto-updates.
 */
export const NotificationCenter: React.FC = () => {
    const { t } = useTranslation("notifications");
    const isAdmin = useCanSeeNotifications();
    const { totalPending } = useAdminNotifications();
    const [open, setOpen] = useState(false);

    const { components: componentTokens } = useDesignSystem();

    // Mirror Header's icon-button styling conventions (badge uses amber).
    const buttonClass = useMemo(() => {
        const base =
            "inline-flex items-center justify-center rounded-full p-1.5 transition-colors duration-200";
        const active = clsx(componentTokens.tabButton.active, "rounded-full border");
        const inactive = clsx(componentTokens.tabButton.inactive, "rounded-full");
        return clsx(base, open ? active : inactive, "relative");
    }, [componentTokens.tabButton.active, componentTokens.tabButton.inactive, open]);

    const badgeClass =
        "absolute -top-1 -end-1 bg-amber-500 text-white text-[9px] font-semibold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1";

    if (!isAdmin) return null;

    return (
        <>
            <button
                type="button"
                className={buttonClass}
                onClick={() => setOpen((s) => !s)}
                title={t("tooltip")}
                aria-label={t("aria_open")}
            >
                <Bell size={16} />
                {totalPending > 0 && (
                    <span
                        className={badgeClass}
                        aria-label={t("badge_aria", { count: totalPending })}
                    >
                        {totalPending > 99 ? "99+" : totalPending}
                    </span>
                )}
            </button>
            {open && <NotificationCenterPanel onClose={() => setOpen(false)} />}
        </>
    );
};

export default NotificationCenter;
